import { CountryCode } from '@/constants/countryCodes';
import { FeatureFlag } from '@/constants/featureFlags';
import { StateCode } from '@/constants/states';
import type { Customer, Pricing } from '@/types/checkout';

const COUNTRIES_WITH_ADDRESS_FIELD_FOR_TAX_CALCULATION = [CountryCode.USA, CountryCode.CANADA];

export const isCountryCodeIsUSAOrCanada = (countryCode: string) => {
  return COUNTRIES_WITH_ADDRESS_FIELD_FOR_TAX_CALCULATION.includes(countryCode as CountryCode);
};

export const isCountryCodeUSA = (countryCode: string) => {
  return countryCode === CountryCode.USA;
};

export const isCaliforniaState = (stateCode: string) => {
  return stateCode === StateCode.CALIFORNIA;
};

export const shouldShowZipCodeFieldForTaxCalculation = (customer: Customer) => {
  return isCountryCodeIsUSAOrCanada(customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '');
};

/**
 * Snapshot predicate evaluated ONCE at boot from the hydrated checkout data.
 * Reads exactly two inputs:
 *   1. `shouldShowZipCodeFieldForTaxCalculation(customer)` — session is
 *      scoped to US/CA (via `customer.taxCountry` or, if absent, IP-derived
 *      `customer.geoLocator.countryCode2`).
 *   2. `pricing.priceBreakdown.isTaxValid === false` — strict equality; a
 *      missing/undefined value yields `false` (i.e. we do NOT snapshot the
 *      wallet-tax fallback when the BE didn't tell us).
 *
 * The BE typically returns `isTaxValid: false` when it couldn't derive a
 * US/CA jurisdiction from pre-existing signals (no persisted
 * `customer.address.zipCode`, IP-based `geoLocator.zipCode` failed to
 * resolve) — that's the ACDEV-27914 target scenario — but this predicate
 * does NOT assert that root cause; it just reflects the BE's flag.
 *
 * The result is stored on `ICheckoutContext.sessionStartedWithoutValidTax`
 * and read by `shouldUseWalletDerivedZipTax` below. It MUST NOT be
 * re-computed mid-session: a wallet-returned ZIP that later flows through
 * `applyWalletTaxToCheckout` will set `isTaxValid` to `true`, but the
 * buyer still entered the checkout unresolved, so the wallet-tax path
 * stays active until the session ends.
 */
export const didSessionStartWithoutValidTax = (customer: Customer, pricing: Pricing | undefined): boolean => {
  return shouldShowZipCodeFieldForTaxCalculation(customer) && pricing?.priceBreakdown?.isTaxValid === false;
};

/**
 * ACDEV-25067 + ACDEV-27914: composite gate for the wallet-derived ZIP + tax
 * flow. True iff the customer is in a tax-relevant jurisdiction (US/CA
 * session gate) AND EITHER:
 *   - the `WALLET_ZIP_TAX` feature flag is ON (ACDEV-25067), OR
 *   - the session started with an unresolved US/CA tax
 *     (`sessionStartedWithoutValidTax`, ACDEV-27914).
 *
 * The second branch treats the "no auto-resolved ZIP at boot" scenario the
 * same as a FF-ON session for the duration of the checkout — the wallet
 * sheet becomes the ZIP input surface instead of forcing the buyer to type
 * one on-page — while leaving buyers who already had a resolved tax on the
 * pre-branch (FF-OFF) UX. FF-OFF is a full kill switch ONLY for sessions
 * that also started with a valid tax.
 *
 * Every wallet-tax entry point (`useWalletZipTaxCalculator`, provider hooks,
 * `getStripeExpressCheckoutShippingConfig`, Adyen options builders) uses
 * THIS predicate — not the raw session/FF gate — so the ACDEV-27914 fallback
 * automatically enables the whole ACDEV-25067 wallet-tax pipeline.
 */
export const shouldUseWalletDerivedZipTax = (
  customer: Customer,
  featureFlags: Record<FeatureFlag, boolean>,
  sessionStartedWithoutValidTax: boolean
): boolean => {
  return (
    (featureFlags[FeatureFlag.WALLET_ZIP_TAX] === true || sessionStartedWithoutValidTax) &&
    shouldShowZipCodeFieldForTaxCalculation(customer)
  );
};

/**
 * Whether the shopper already has a real, persisted ZIP/postal code on file.
 *
 * Reads only `customer.address.zipCode` — the shopper-provided value the BE
 * persists across sessions (and the same source `CheckoutContent` hydrates
 * `addressDetails` from at boot). The IP-derived `geoLocator.zipCode` is
 * intentionally excluded: it's an unconfirmed guess, so we don't treat it as
 * "the shopper has a ZIP".
 *
 * Used by templates that hide the ZIP input when it's redundant (PREFERRED /
 * last-payment), gated behind `TemplateFeatures.hidePrefilledZipCode`.
 */
export const hasStoredZipCode = (customer: Customer): boolean => {
  return Boolean(customer.address?.zipCode?.trim());
};

export const shouldCollectZipCodeForTaxCalculation = (
  customer: Customer,
  zipCode: string | null | undefined,
  isTaxValid: boolean | undefined
) => {
  return shouldShowZipCodeFieldForTaxCalculation(customer) && (!zipCode?.trim() || !isTaxValid);
};
